/**
 * HTTP Client wrapper for API requests
 * Handles authentication, retries, and error handling
 */

import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { NetworkError, AuthenticationError } from './errors';
import { config } from './config';

export interface RequestOptions extends AxiosRequestConfig {
  retryAttempts?: number;
  authRequired?: boolean;
}

export class HTTPClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private tokenExpiry: number = 0;

  constructor(baseURL?: string) {
    this.client = axios.create({
      baseURL: baseURL || config.get('baseURL'),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Request interceptor for auth
    this.client.interceptors.request.use(
      async (config) => {
        if (config.headers && this.accessToken && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Token expired, try to refresh
          this.accessToken = null;
          throw new AuthenticationError('Authentication failed', { 
            status: error.response.status 
          });
        }
        
        if (error.response) {
          throw new NetworkError(
            error.response.data?.message || error.message,
            error.response.status,
            { originalError: error }
          );
        }
        
        throw new NetworkError('Network request failed', 0, { originalError: error });
      }
    );
  }

  /**
   * Set access token for authenticated requests
   */
  setAccessToken(token: string, expiresIn: number = 3600): void {
    this.accessToken = token;
    this.tokenExpiry = Date.now() + (expiresIn * 1000);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    return Date.now() >= this.tokenExpiry;
  }

  /**
   * Clear access token
   */
  clearToken(): void {
    this.accessToken = null;
    this.tokenExpiry = 0;
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, options?: RequestOptions): Promise<T> {
    const response = await this.client.get<T>(url, options);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    const response = await this.client.post<T>(url, data, options);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    const response = await this.client.put<T>(url, data, options);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    const response = await this.client.patch<T>(url, data, options);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, options?: RequestOptions): Promise<T> {
    const response = await this.client.delete<T>(url, options);
    return response.data;
  }

  /**
   * Get raw axios instance for advanced use cases
   */
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Create singleton instance
export const httpClient = new HTTPClient();